/**
 * 學習進度同步工具
 * 負責將本地學習進度同步到後端資料庫
 */

// 節流：避免過於頻繁的同步請求
let syncTimer: NodeJS.Timeout | null = null
const SYNC_DELAY = 2000 // 2 秒延遲
const SYNC_INTERVAL = 5 * 60 * 1000 // 最多每 5 分鐘同步一次
let lastSyncTime = 0

/**
 * 上傳單種類型的學習進度到後端
 */
export async function syncProgressToServer(
  itemType: 'vocabulary' | 'kanji' | 'grammar' | 'gojuon',
  progressData: any
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/study/progress/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        [itemType]: progressData
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }

    const result = await response.json()
    return { success: result.success || false }
  } catch (error) {
    console.error(`[Progress Sync] 同步 ${itemType} 失敗:`, error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    }
  }
}

/**
 * 上傳所有學習進度到後端（批量）
 */
export async function syncAllProgressToServer(progress: {
  vocabulary?: any
  kanji?: any
  grammar?: any
  gojuon?: any
}): Promise<{ success: boolean; synced?: Record<string, number>; error?: string }> {
  try {
    const response = await fetch('/api/study/progress/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(progress)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }

    const result = await response.json()
    return { 
      success: result.success || false,
      synced: result.synced
    }
  } catch (error) {
    console.error('[Progress Sync] 批量同步失敗:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    }
  }
}

/**
 * 從後端下載所有學習進度
 */
export async function downloadProgressFromServer(): Promise<{
  success: boolean
  data?: {
    vocabulary?: any
    kanji?: any
    grammar?: any
    gojuon?: any
  }
  error?: string
}> {
  try {
    const response = await fetch('/api/study/progress/download', {
      method: 'GET',
      credentials: 'include'
    })

    if (!response.ok) {
      if (response.status === 401) {
        // 未登入，返回空資料
        return { success: true, data: {} }
      }
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }

    const result = await response.json()
    return { 
      success: result.success || false,
      data: result.data || {}
    }
  } catch (error) {
    console.error('[Progress Sync] 下載進度失敗:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    }
  }
}

/**
 * 節流同步：延遲執行，避免過於頻繁
 */
export function debouncedSync(
  itemType: 'vocabulary' | 'kanji' | 'grammar' | 'gojuon',
  progressData: any
): void {
  // 清除之前的計時器
  if (syncTimer) {
    clearTimeout(syncTimer)
  }

  // 檢查是否在節流時間內
  const now = Date.now()
  if (now - lastSyncTime < SYNC_INTERVAL) {
    // 在節流時間內，延遲執行
    syncTimer = setTimeout(async () => {
      await syncProgressToServer(itemType, progressData)
      lastSyncTime = Date.now()
      syncTimer = null
    }, SYNC_DELAY)
  } else {
    // 超過節流時間，立即執行
    syncProgressToServer(itemType, progressData).then(() => {
      lastSyncTime = Date.now()
    })
  }
}

/**
 * 合併本地和遠端進度（以最新的為準）
 */
export function mergeProgress(local: any, remote: any, itemType: string): any {
  if (!remote || Object.keys(remote).length === 0) {
    return local || {}
  }

  if (!local || Object.keys(local).length === 0) {
    return remote || {}
  }

  // 合併邏輯：以最新的為準
  // 對於每個項目，比較 lastReview 或 lastUpdated 時間戳
  const merged: any = { ...local }

  for (const [itemId, remoteProgress] of Object.entries(remote)) {
    const localProgress = local[itemId]
    
    if (!localProgress) {
      // 本地沒有，使用遠端（需要轉換日期字串為 Date 物件）
      merged[itemId] = convertProgressDates(remoteProgress as any, itemType)
    } else {
      // 比較時間戳，以最新的為準
      const localTime = getProgressTimestamp(localProgress, itemType)
      const remoteTime = getProgressTimestamp(remoteProgress as any, itemType)
      
      if (remoteTime > localTime) {
        // 遠端更新，使用遠端（需要轉換日期字串為 Date 物件）
        merged[itemId] = convertProgressDates(remoteProgress as any, itemType)
      }
      // 否則保留本地（已經在 merged 中）
    }
  }

  return merged
}

/**
 * 轉換進度資料中的日期字串為 Date 物件
 */
function convertProgressDates(progress: any, itemType: string): any {
  if (!progress || typeof progress !== 'object') {
    return progress
  }

  const converted = { ...progress }

  // 對於 kanji 和 grammar，轉換 lastReview
  if ((itemType === 'kanji' || itemType === 'grammar') && progress.lastReview) {
    if (typeof progress.lastReview === 'string') {
      converted.lastReview = new Date(progress.lastReview)
    }
  }

  return converted
}

/**
 * 取得進度的時間戳
 */
function getProgressTimestamp(progress: any, itemType: string): number {
  if (itemType === 'gojuon') {
    return progress.lastUpdated || 0
  } else if (itemType === 'vocabulary') {
    return progress.lastReview || 0
  } else if (itemType === 'kanji' || itemType === 'grammar') {
    if (progress.lastReview instanceof Date) {
      return progress.lastReview.getTime()
    }
    if (typeof progress.lastReview === 'string') {
      return new Date(progress.lastReview).getTime()
    }
    return 0
  }
  return 0
}

